import {
  Button,
  Sheet,
  SheetContent,
  SheetTitle,
  SheetDescription,
  Dialog,
  DialogPortal,
  DialogContent,
  DialogTitle,
  DialogDescription,
  mediaQueries,
} from "@mozioinc/ui";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "usehooks-ts";
import { useSessionStore } from "../stores/session-store";
import { redirectToLogin } from "../utils/auth";
import AlertSvg from "./assets/alert.svg?react";
import type { TransNamespace } from "../types";
import type { ComponentType, ReactNode } from "react";

/**
 * Shown when a session expires and cannot be refreshed. Deliberately has no way
 * to dismiss it: signing back in is the only way to continue, and letting the
 * user close it would drop them into a booking flow whose requests all fail.
 *
 * Travel agents must never complete a booking as an anonymous user — they would
 * lose their commission — so blocking the flow is the point, not a side effect.
 */
export const SessionExpiredDialog = () => {
  const { t } = useTranslation<TransNamespace>("transfer:session-expired");
  const greaterThanLarge = useMediaQuery(mediaQueries.lg);
  const isExpired = useSessionStore((state) => state.isExpired);

  if (!isExpired) {
    return null;
  }

  // Radix requires a Title and Description inside Dialog.Content, or screen
  // readers get an unlabelled modal. Sheet is built on the same primitive, so
  // each branch has to supply its own — hence passing the components in.
  const renderContent = (
    Title: ComponentType<{ className?: string; children: ReactNode }>,
    Description: ComponentType<{ className?: string; children: ReactNode }>,
  ) => (
    <div>
      <div className="mx-auto -mt-20 mb-5 flex h-[7.5rem] w-[7.5rem] items-center justify-center rounded-full bg-primary">
        <AlertSvg className="text-6xl text-primary-contrast" />
      </div>
      <Title className="mb-2 text-center text-lg font-bold leading-6 tracking-normal lg:mb-3">
        {t("Your session has expired")}
      </Title>
      <Description className="p-0 text-center text-sm text-neutral-800">
        {t(
          "For your security, you've been signed out. Sign in again to finish your booking.",
        )}
      </Description>
      <div className="mt-8 flex gap-2">
        <Button
          onClick={redirectToLogin}
          type="button"
          className="flex-1 outline-none"
        >
          {t("Sign in to continue")}
        </Button>
      </div>
      <p className="my-3 text-center text-sm text-neutral-800">
        {t("Your booking details are saved.")}
      </p>
    </div>
  );

  return (
    <>
      <div className="hidden lg:block">
        <Dialog open={greaterThanLarge}>
          <DialogPortal>
            <DialogContent className="max-h-full max-w-sm p-5">
              {renderContent(DialogTitle, DialogDescription)}
            </DialogContent>
          </DialogPortal>
        </Dialog>
      </div>
      <div className="lg:hidden">
        <Sheet open={!greaterThanLarge}>
          <SheetContent
            side={"bottom"}
            overflow="visible"
            className="flex min-h-fit flex-col rounded-lg p-4"
          >
            {renderContent(SheetTitle, SheetDescription)}
          </SheetContent>
        </Sheet>
      </div>
    </>
  );
};
